import { Identifier, PaginationPayload, SortPayload } from "react-admin";

import { storage } from "../../storage";
import { Region, Resource } from "../../types";
import { env } from "../../utils";
import { apiUtils } from ".";
import { buildBaseUrlFor } from "./BaseUrlForResourceBuilder";
import { getManyReferences } from "./GetManyReferencesBuilder";

const urlBuilder = {
  // ====================================================================
  // Methods to help build absolute URLs targetting a particular backend
  // ====================================================================

  /**
   * Builds the absolute URL based on the passed in region & relative URL
   */
  regional: (region: Region, relativeUrl: string): string => {
    return `${storage.getApiBaseUrlForRegion(region)}/${relativeUrl}`;
  },

  /**
   * Builds the absolute super service URL based on the relative URL
   */
  super: (relativeUrl: string): string => {
    return `${env.base_urls.fetch_for_super()}/${relativeUrl}`;
  },

  // ====================================================================
  // Methods to help build absolute URLs for `DataProvider` methods
  // ====================================================================

  /**
   * Builds the absolute URL to make a POST call that creates a single item
   */
  create: (resource: string): string => {
    return buildBaseUrlFor(resource);
  },

  /**
   * Builds the absolute URL to make a PATCH call that updates a single item
   * for the passed in ID
   */
  update: (resource: string, id: Identifier): string => {
    // The OTC maintenance window is a singleton resource.
    // We interact with it differently, without an ID.
    if (resource === Resource.OtcMaintenanceWindow) {
      return buildBaseUrlFor(resource);
    }

    return `${buildBaseUrlFor(resource)}/${id}`;
  },

  /**
   * Builds the absolute URL to make a DELETE call that deletes a single item
   * for the passed in ID
   */
  delete: (resource: string, id: Identifier): string => {
    return `${buildBaseUrlFor(resource)}/${id}`;
  },

  /**
   * Builds the absolute URL to make a GET call that returns a single item
   * for the passed in ID
   */
  getOne: (resource: string, id: Identifier): string => {
    // The OTC maintenance window is a singleton resource.
    // We fetch it differently, without an ID.
    if (resource === Resource.OtcMaintenanceWindow) {
      return buildBaseUrlFor(resource);
    }

    return `${buildBaseUrlFor(resource)}/${id}`;
  },

  /**
   * Builds the absolute URL to make a GET call that returns a list of
   * items based on the passed in IDs
   */
  getMany: (resource: string, ids: Identifier[]): string => {
    const queryParams = apiUtils.queryParametersStringify({ ids });
    return `${buildBaseUrlFor(resource)}?${queryParams}`;
  },

  /**
   * Builds the absolute URL to make a GET call that returns a list of
   * items based on the passed in filters
   */
  getList: (
    resource: string,
    filter: any,
    sort: SortPayload,
    pagination: PaginationPayload,
  ): string => {
    // The OTC maintenance window is a singleton resource.
    // We fetch it differently, without filters, pagination or sorts.
    if (resource === Resource.OtcMaintenanceWindow) {
      return buildBaseUrlFor(resource);
    }

    const queryParams = apiUtils.queryParametersStringify({
      filters: apiUtils.formatFilters(filter),
      page: pagination.page,
      per_page: pagination.perPage,
      sorts: apiUtils.formatSorts(sort),
    });

    return `${buildBaseUrlFor(resource)}?${queryParams}`;
  },

  /**
   * Builds the absolute URL to make a GET call that returns a list of
   * @resource items associated to the passed in @target with @id
   */
  getManyReferences,
};

export default urlBuilder;
